import React, { useState } from 'react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { MessageCircle, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      setStatus({ type: 'success', message: data.message });
      setForm({ name: '', email: '', message: '' });
    } catch {
      setStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-20 min-h-screen">
      <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 lg:gap-24">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.3em] text-[#C6A978] mb-6">Get in Touch</p>
              <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-light tracking-tighter text-[#1E1C1A] mb-8 leading-[1.1]">
                Contact Us
              </h1>
              <p className="text-base font-light leading-relaxed tracking-wide text-[#7A7369] mb-10">
                We'd love to hear from you. Whether you're looking for a specific piece, interested in a custom design, 
                or simply want to learn more about KIRAQ — we're here.
              </p>

              <div className="space-y-6 mb-10">
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-[#1E1C1A] mb-2">Email</h3>
                  <p className="text-sm font-light text-[#7A7369]">kiraqindia@gmail.com</p>
                </div>
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-[#1E1C1A] mb-2">WhatsApp</h3>
                  <p className="text-sm font-light text-[#7A7369]">+91 9780411102</p>
                </div>
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-[#1E1C1A] mb-2">Hours</h3>
                  <p className="text-sm font-light text-[#7A7369]">Monday – Saturday, 10am – 7pm IST</p>
                </div>
              </div>

              <a
                href="https://wa.me/919780411102?text=Hi%20KIRAQ%2C%20I%20have%20a%20question"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="inline-flex items-center gap-2 border border-[#1E1C1A] text-[#1E1C1A] bg-transparent hover:bg-[#1E1C1A] hover:text-[#F6F3EE] px-6 py-3 text-xs tracking-[0.15em] uppercase transition-all duration-300"
              >
                <MessageCircle size={14} /> Chat on WhatsApp
              </a>
            </div>

            <div className="bg-[#E8E1D8] p-8 md:p-10">
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-xs font-medium uppercase tracking-[0.15em] text-[#1E1C1A] mb-2 block">Name</Label>
                  <Input
                    id="name"
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    required
                    className="bg-[#F6F3EE] border-[#DDD7CE] focus:border-[#C6A978] focus:ring-[#C6A978]/20 text-[#1E1C1A] font-light"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-xs font-medium uppercase tracking-[0.15em] text-[#1E1C1A] mb-2 block">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-email-input"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    required
                    className="bg-[#F6F3EE] border-[#DDD7CE] focus:border-[#C6A978] focus:ring-[#C6A978]/20 text-[#1E1C1A] font-light"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <Label htmlFor="message" className="text-xs font-medium uppercase tracking-[0.15em] text-[#1E1C1A] mb-2 block">Message</Label>
                  <Textarea
                    id="message"
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={e => setForm({ ...form, message: e.target.value })}
                    required
                    rows={5}
                    className="bg-[#F6F3EE] border-[#DDD7CE] focus:border-[#C6A978] focus:ring-[#C6A978]/20 text-[#1E1C1A] font-light resize-none"
                    placeholder="Tell us how we can help..."
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={loading}
                  className="w-full bg-[#1E1C1A] text-[#F6F3EE] py-3.5 text-xs tracking-[0.2em] uppercase hover:bg-[#1E1C1A] transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loading ? 'Sending...' : <><Send size={13} /> Send Message</>}
                </button>
                {status && (
                  <p data-testid="contact-status" className={`text-sm text-center ${status.type === 'success' ? 'text-green-700' : 'text-red-600'}`}>
                    {status.message}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
